/**
 * "Email me a sign-in link", under the Google button on `/login` (ORDR-189).
 *
 * For staff whose mailbox is not Google: a reseller admin with a Yahoo address
 * accepted their invitation and then had no way in.
 *
 * `useEmailLinkLogin` from `@pirouz-ca/ui-auth` owns the request: the POST to
 * `/login/link`, the single in-flight guard, and the mapping of 202 to `sent`
 * and of BAD_TARGET, any other refusal or a dropped connection to `failed`.
 * This file owns the copy. `LoginScreen`'s built-in link form is not used
 * because its sent message cannot name the address, and a member of staff who
 * typed a stale address needs to see which one the link went to.
 *
 * THE SENT STATE CLAIMS NOTHING ABOUT AN ACCOUNT. paio-auth answers 202 for
 * every address so that the form cannot be used to enumerate staff, and the
 * copy says "if this address can sign in" to match.
 *
 * Styling follows the `LoginScreen` card it sits in, through the same design
 * tokens, so the screen keeps its look.
 */

import { useId, useState, type FormEvent, type ReactElement } from "react";
import { EmailLinkIcon, useEmailLinkLogin, type AuthConfig } from "@pirouz-ca/ui-auth";
import { writeLinkReturnPath } from "../auth/linkReturnPath.js";

export interface EmailLinkFormProps {
  /** The `/login` config, with `emailLink` set: `authConfig` decides that. */
  readonly config: AuthConfig;
  /** The validated return path, put down for `/auth/link/<token>` to take. */
  readonly returnPath: string;
}

const INPUT =
  "block w-full rounded-[var(--border-radius-md)] border border-[var(--color-neutral-300)] " +
  "bg-[var(--color-neutral-50)] px-3 py-2 text-base text-[var(--color-neutral-900)] " +
  "placeholder:text-[var(--color-neutral-400)] focus:border-[var(--color-primary-500)] " +
  "focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-200)]";

const BUTTON =
  "flex w-full items-center justify-center gap-2 rounded-[var(--border-radius-md)] " +
  "border border-[var(--color-neutral-300)] bg-[var(--color-neutral-50)] px-4 py-2.5 text-base " +
  "font-medium text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-100)] " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary-500)] " +
  "disabled:cursor-not-allowed disabled:opacity-60";

const ALERT =
  "rounded-[var(--border-radius-md)] border border-[var(--color-error-200)] " +
  "bg-[var(--color-error-100)] px-4 py-2.5 text-sm text-[var(--color-error-800)]";

export function EmailLinkForm({ config, returnPath }: EmailLinkFormProps): ReactElement {
  const { state, request, reset } = useEmailLinkLogin(config);
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const inputId = useId();
  const sending = state === "sending";

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const address = email.trim();
    setSentTo(address);
    writeLinkReturnPath(returnPath);
    void request(address);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 text-sm text-[var(--color-neutral-500)]" aria-hidden="true">
        <span className="h-px flex-1 bg-current opacity-25" />
        <span>or</span>
        <span className="h-px flex-1 bg-current opacity-25" />
      </div>
      {state === "sent" ? (
        <div data-testid="email-link-sent" role="status" className="flex flex-col gap-3 text-sm text-[var(--color-neutral-700)]">
          <p>
            If <strong className="break-all text-[var(--color-neutral-900)]">{sentTo}</strong> can sign in to
            Ordering, a link is on its way. It works for 10 minutes.
          </p>
          <p>If it does not arrive in a few minutes, check your spam or junk folder.</p>
          <button type="button" onClick={reset} className="self-start font-medium underline">
            Use a different address
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {state === "failed" && (
            <p data-testid="email-link-failed" role="alert" className={ALERT}>
              We could not send a sign-in link. Check the address and try again.
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            <label htmlFor={inputId} className="text-sm font-medium text-[var(--color-neutral-700)]">
              Email address
            </label>
            <input
              id={inputId}
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              className={INPUT}
            />
          </div>
          <button type="submit" disabled={sending} className={BUTTON}>
            <EmailLinkIcon />
            {sending ? "Sending your link" : "Email me a sign-in link"}
          </button>
        </form>
      )}
    </div>
  );
}
